import React, { useState, useEffect, useRef } from 'react';
import { FormField, Input, Button } from '../ui';
import { ContactInfo } from '../../types';
import { api } from '../../services/api';

interface ContactInfoFormProps {
  data: ContactInfo;
  onChange: (data: Partial<ContactInfo>) => void;
  errors: Record<string, string>;
  country?: string;
  city?: string;
  domain?: string;
}

export const ContactInfoForm: React.FC<ContactInfoFormProps> = React.memo(({
  data,
  onChange,
  errors,
  country,
  city,
  domain,
}) => {
  const [generatingPhone, setGeneratingPhone] = useState(false);
  const hasAutoPopulated = useRef(false);

  // Store initial values in refs to use in effect without causing re-runs
  const initialDataRef = useRef(data);
  const propsRef = useRef({ domain, country, city, onChange });

  // Update refs on each render
  propsRef.current = { domain, country, city, onChange };

  // Auto-populate all empty fields on mount only
  useEffect(() => {
    if (hasAutoPopulated.current) return;
    hasAutoPopulated.current = true;

    let cancelled = false;

    const autoPopulateAll = async () => {
      const updates: Partial<ContactInfo> = {};
      const { domain: d, country: c, city: ct, onChange: oc } = propsRef.current;
      const initialData = initialDataRef.current;

      // Auto-populate emails if domain is available
      if (d && !initialData.primaryEmail) {
        updates.primaryEmail = `contact@${d}`;
        updates.supportEmail = `support@${d}`;
        updates.legalEmail = `legal@${d}`;
        updates.privacyEmail = `privacy@${d}`;
      }

      // Auto-populate business hours
      if (!initialData.businessHours) {
        updates.businessHours = 'Mon-Fri: 9:00 AM - 6:00 PM';
      }

      // Auto-populate phone if country/city available and phone is empty
      if (c && ct && !initialData.phone) {
        try {
          setGeneratingPhone(true);
          const autoData = await api.autoGenerate({ country: c, city: ct });
          if (!cancelled) {
            updates.phone = autoData.data.phone;
          }
        } catch (error) {
          if (!cancelled && !(error instanceof Error && error.name === 'AbortError')) {
            console.error('Failed to auto-generate phone:', error);
          }
        } finally {
          if (!cancelled) {
            setGeneratingPhone(false);
          }
        }
      }

      if (!cancelled && Object.keys(updates).length > 0) {
        oc(updates);
      }
    };

    autoPopulateAll();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally run only on mount - refs provide access to current values

  const handleRegeneratePhone = async () => {
    if (!country || !city) return;

    setGeneratingPhone(true);
    try {
      const autoData = await api.autoGenerate({ country, city });
      onChange({ phone: autoData.data.phone });
    } catch (error) {
      console.error('Failed to regenerate phone:', error);
    } finally {
      setGeneratingPhone(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <FormField
          label="Primary Business Email"
          required
          error={errors.primaryEmail}
          htmlFor="primary-email"
          hint="Must be a business domain (not Gmail, Yahoo, etc.)"
        >
          <Input
            id="primary-email"
            type="email"
            value={data.primaryEmail}
            onChange={(e) => onChange({ primaryEmail: e.target.value })}
            placeholder="contact@yourcompany.com"
            error={errors.primaryEmail}
          />
        </FormField>

        <FormField
          label="Support Email"
          required
          error={errors.supportEmail}
          htmlFor="support-email"
        >
          <Input
            id="support-email"
            type="email"
            value={data.supportEmail}
            onChange={(e) => onChange({ supportEmail: e.target.value })}
            placeholder="support@yourcompany.com"
            error={errors.supportEmail}
          />
        </FormField>

        <FormField
          label="Legal Email"
          required
          error={errors.legalEmail}
          htmlFor="legal-email"
        >
          <Input
            id="legal-email"
            type="email"
            value={data.legalEmail}
            onChange={(e) => onChange({ legalEmail: e.target.value })}
            placeholder="legal@yourcompany.com"
            error={errors.legalEmail}
          />
        </FormField>

        <FormField
          label="Privacy Email"
          required
          error={errors.privacyEmail}
          htmlFor="privacy-email"
        >
          <Input
            id="privacy-email"
            type="email"
            value={data.privacyEmail}
            onChange={(e) => onChange({ privacyEmail: e.target.value })}
            placeholder="privacy@yourcompany.com"
            error={errors.privacyEmail}
          />
        </FormField>

        <FormField
          label="Phone Number"
          required
          error={errors.phone}
          htmlFor="phone"
          hint="Include country code (e.g., +49 30 1234567)"
        >
          <div className="flex gap-2">
            <div style={{ flex: 1 }}>
              <Input
                id="phone"
                type="tel"
                value={data.phone}
                onChange={(e) => onChange({ phone: e.target.value })}
                placeholder="+49 30 1234567"
                error={errors.phone}
                disabled={generatingPhone}
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="md"
              onClick={handleRegeneratePhone}
              disabled={!country || !city || generatingPhone}
              loading={generatingPhone}
              title="Regenerate phone number"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                <path d="M3 3v5h5" />
                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                <path d="M16 16h5v5" />
              </svg>
            </Button>
          </div>
        </FormField>

        <FormField
          label="Business Hours"
          required
          error={errors.businessHours}
          htmlFor="business-hours"
        >
          <Input
            id="business-hours"
            type="text"
            value={data.businessHours}
            onChange={(e) => onChange({ businessHours: e.target.value })}
            placeholder="Mon-Fri: 9:00 AM - 6:00 PM"
            error={errors.businessHours}
          />
        </FormField>
      </div>
    </div>
  );
});

ContactInfoForm.displayName = 'ContactInfoForm';

export default ContactInfoForm;
